import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { SystemInfoBox, SystemInformationSection } from '../../components/ui/SystemInformation';
import {
  createSubsidiaryTask,
  getSubsidiaryTaskById,
  SUBSIDIARY_TASK_DESCRIPTION_MAX_LENGTH,
  SUBSIDIARY_TASK_NAME_MAX_LENGTH,
  updateSubsidiaryTask,
} from '../../services/subsidiaryTaskMasters';
import { setFlashToast } from '../../utils/flashToast';
import { formatDocumentTableDateTime } from '../../utils/dateFormat';
import {
  focusFirstInvalid,
  validateRequired,
  type RequiredErrors,
  type RequiredFieldSpec,
} from '../../utils/requiredValidation';
import { useAuth } from '../../contexts/AuthContext';
import { exceedsMax, maxCharLimitExceededError } from '../../utils/caseFieldValidation';

const FIELD_ERR_STYLE = { fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 } as const;

export default function SubsidiaryTaskFormPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { subsidiaryTaskId } = useParams();
  const isEdit = Boolean(subsidiaryTaskId);
  const nav = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [createdAt, setCreatedAt] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');

  const clearReqError = (key: string) => {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (!isEdit || !subsidiaryTaskId || !user) return;
    let alive = true;
    setLoading(true);
    void getSubsidiaryTaskById(subsidiaryTaskId).then((res) => {
      if (!alive) return;
      if (res.success) {
        const d = res.data;
        setName(d.name);
        setDescription(d.description);
        setActive(d.active);
        setCreatedAt(d.createdAt);
        setCreatedBy(d.createdBy);
        setUpdatedAt(d.updatedAt);
        setUpdatedBy(d.updatedBy);
      } else {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isEdit, subsidiaryTaskId, t, user?.id]);

  const requiredSpecs = useMemo<RequiredFieldSpec[]>(() => {
    return [
      {
        key: 'name',
        label: t('metadata.subsidiaryTasks.subsidiaryTask'),
        value: name,
        elementId: 'subsidiary-task-name',
      },
    ];
  }, [name, t]);

  const validationSpecs = useMemo<RequiredFieldSpec[]>(() => {
    return [
      ...requiredSpecs,
      {
        key: 'description',
        label: t('metadata.subsidiaryTasks.description'),
        value: description,
        elementId: 'subsidiary-task-description',
      },
    ];
  }, [description, requiredSpecs, t]);

  async function handleSave() {
    if (savingRef.current) return;

    const errs = validateRequired(requiredSpecs);
    if (exceedsMax(name, SUBSIDIARY_TASK_NAME_MAX_LENGTH)) {
      errs.name = maxCharLimitExceededError();
    }
    if (exceedsMax(description, SUBSIDIARY_TASK_DESCRIPTION_MAX_LENGTH)) {
      errs.description = maxCharLimitExceededError();
    }
    setReqErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstInvalid(validationSpecs, errs);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    let keepLocked = false;
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        active,
      };
      const res =
        isEdit && subsidiaryTaskId
          ? await updateSubsidiaryTask(subsidiaryTaskId, payload)
          : await createSubsidiaryTask(payload);
      if (!res.success) {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
        return;
      }
      setFlashToast({
        message: isEdit
          ? t('metadata.subsidiaryTaskForm.updateSuccess')
          : t('metadata.subsidiaryTaskForm.createSuccess'),
        variant: 'success',
      });
      nav('/metadata/subsidiary-tasks');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        <Link to="/metadata/subsidiary-tasks">{t('metadata.subsidiaryTasks.title')}</Link>
        <span className="bc-sep">/</span>
        {isEdit
          ? t('metadata.subsidiaryTaskForm.updateTitle')
          : t('metadata.subsidiaryTaskForm.addTitle')}
      </div>

      <div className="pg-title">
        {isEdit
          ? t('metadata.subsidiaryTaskForm.updateTitle')
          : t('metadata.subsidiaryTaskForm.addTitle')}
      </div>

      <div className="cr-table-wrap" style={{ overflow: 'visible' }}>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}
          {reqErrors.form ? <div className="si-err" style={{ marginBottom: 12 }}>{reqErrors.form}</div> : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <div className="al-l">
                {t('metadata.subsidiaryTasks.subsidiaryTask')} <span className="req">*</span>
              </div>
              <input
                id="subsidiary-task-name"
                className="al-inp"
                value={name}
                maxLength={SUBSIDIARY_TASK_NAME_MAX_LENGTH}
                placeholder={t('metadata.subsidiaryTaskForm.namePlaceholder')}
                disabled={loading}
                style={reqErrors.name ? { borderColor: 'var(--red)' } : undefined}
                aria-invalid={reqErrors.name ? true : undefined}
                onChange={(e) => {
                  setName(e.target.value);
                  clearReqError('name');
                }}
              />
              {reqErrors.name ? <div style={FIELD_ERR_STYLE}>{reqErrors.name}</div> : null}
            </div>

            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <div className="al-l">{t('metadata.subsidiaryTasks.description')}</div>
              <textarea
                id="subsidiary-task-description"
                className="al-inp"
                style={{
                  height: 90,
                  paddingTop: 10,
                  paddingBottom: 10,
                  resize: 'vertical',
                  ...(reqErrors.description ? { borderColor: 'var(--red)' } : {}),
                }}
                value={description}
                maxLength={SUBSIDIARY_TASK_DESCRIPTION_MAX_LENGTH}
                placeholder={t('metadata.subsidiaryTaskForm.descriptionPlaceholder')}
                disabled={loading}
                aria-invalid={reqErrors.description ? true : undefined}
                onChange={(e) => {
                  setDescription(e.target.value);
                  clearReqError('description');
                }}
              />
              {reqErrors.description ? <div style={FIELD_ERR_STYLE}>{reqErrors.description}</div> : null}
            </div>

            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <label className="ccr-chk" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={active}
                  disabled={loading}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>{t('common.active')}</span>
              </label>
            </div>
          </div>

          {isEdit ? (
            <SystemInformationSection title={t('profile.systemInformation')}>
              <SystemInfoBox
                label={t('profile.createdAt')}
                value={formatDocumentTableDateTime(createdAt) || createdAt}
              />
              <SystemInfoBox label={t('profile.createdBy')} value={createdBy} />
              <SystemInfoBox
                label={t('profile.lastUpdatedAt')}
                value={formatDocumentTableDateTime(updatedAt) || updatedAt}
              />
              <SystemInfoBox label={t('profile.lastUpdatedBy')} value={updatedBy} />
            </SystemInformationSection>
          ) : null}
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving || loading}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('metadata.subsidiaryTaskForm.cancelConfirm')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav('/metadata/subsidiary-tasks');
        }}
      />
    </>
  );
}
